import { useState } from 'react'
import { RefreshCw, Trophy } from 'lucide-react'
import { useLiveScores } from '../../hooks/useLiveScores'
import SportSelector from '../ui/SportSelector'
import GameCard from './GameCard'
import LoadingSpinner from '../ui/LoadingSpinner'
import type { Sport } from '../../types'

export default function LiveScoresDashboard() {
  const [selectedSport, setSelectedSport] = useState<Sport | 'ALL'>('ALL')
  const sport = selectedSport === 'ALL' ? undefined : selectedSport
  const { data: games, isLoading, error, refetch, dataUpdatedAt } = useLiveScores(sport)

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Live Scores</h1>
          <p className="text-sm text-gray-500">
            Real-time scores from across the sports world
          </p>
        </div>
        <div className="flex items-center gap-3">
          {dataUpdatedAt && (
            <span className="text-xs text-gray-400">
              Updated {new Date(dataUpdatedAt).toLocaleTimeString()}
            </span>
          )}
          <button
            onClick={() => refetch()}
            disabled={isLoading}
            className="btn-secondary p-2"
            title="Refresh scores"
          >
            <RefreshCw className={`w-4 h-4 ${isLoading ? 'animate-spin' : ''}`} />
          </button>
        </div>
      </div>

      {/* Sport filter */}
      <SportSelector
        selected={selectedSport}
        onChange={setSelectedSport}
        showAll={true}
      />

      {/* Content */}
      {isLoading && !games ? (
        <div className="flex items-center justify-center py-20">
          <LoadingSpinner size="lg" />
        </div>
      ) : error ? (
        <ErrorState onRetry={() => refetch()} />
      ) : games && games.length > 0 ? (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {games.map((game) => (
            <GameCard key={game.id} game={game} />
          ))}
        </div>
      ) : (
        <EmptyState sport={selectedSport} />
      )}
    </div>
  )
}

interface EmptyStateProps {
  sport: Sport | 'ALL'
}

function EmptyState({ sport }: EmptyStateProps) {
  return (
    <div className="card p-12 text-center">
      <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
        <Trophy className="w-8 h-8 text-gray-400" />
      </div>
      <h3 className="text-lg font-medium text-gray-900 mb-2">No Live Games</h3>
      <p className="text-gray-500">
        {sport === 'ALL'
          ? 'There are no games in progress right now.'
          : `There are no ${sport} games in progress right now.`}
      </p>
      <p className="text-sm text-gray-400 mt-2">
        Check back later or view the schedule for upcoming games.
      </p>
    </div>
  )
}

interface ErrorStateProps {
  onRetry: () => void
}

function ErrorState({ onRetry }: ErrorStateProps) {
  return (
    <div className="card p-12 text-center">
      <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
        <Trophy className="w-8 h-8 text-red-400" />
      </div>
      <h3 className="text-lg font-medium text-gray-900 mb-2">Unable to Load Scores</h3>
      <p className="text-gray-500 mb-4">
        We couldn't fetch the latest scores. Please try again.
      </p>
      <button onClick={onRetry} className="btn-primary">
        Try Again
      </button>
    </div>
  )
}
